<template>
  <div
    v-if="hasHiddenCard"
    class="pagination"
  >
    <div
      v-for="(isVisible, index) in visibleCards"
      :key="`pag-${index}`"
      class="pagination-item"
      :class="{ disabled: isVisible }"
      @click="goTo(index, isVisible)"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, nextTick, onUnmounted, ComponentPublicInstance, computed } from 'vue';
import { debounce } from '@/utils';

const props = defineProps<{
  carouselRef: ComponentPublicInstance | undefined;
}>();

const visibleCards = ref<boolean[]>([]);
const hasHiddenCard = computed(() => visibleCards.value.includes(false));

const isElementVisible = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect();
  const parentRect = props.carouselRef?.$el.getBoundingClientRect();

  return (
    rect.top >= parentRect.top
      && rect.left >= parentRect.left
      && rect.bottom <= parentRect.bottom
      && rect.right <= parentRect.right
  );
};

const updVisibleCards = debounce(() => {
  const items: HTMLElement[] = props.carouselRef?.$el.querySelectorAll('.slideEl');
  nextTick(() => {
    visibleCards.value = Array.from(items).map((item) => isElementVisible(item));
  });
}, 100);

const resizeAction = () => {
  updVisibleCards();
};

const findIndex = () => visibleCards.value.findIndex((x: boolean) => x);

const goTo = (index: number, isVisible: boolean) => {
  if (isVisible) { return; }
  const counterActivePoints = visibleCards.value.filter(Boolean).length;
  const currentIndex = findIndex();
  const adjustedIndex = index > currentIndex ? index - counterActivePoints + 1 : index;
  props.carouselRef?.slideTo(adjustedIndex);
};

onMounted(() => {
  updVisibleCards();
  window.addEventListener('resize', resizeAction);
});

onUnmounted(() => {
  window.removeEventListener('resize', resizeAction);
});

defineExpose({
  updVisibleCards,
  hasHiddenCard,
  visibleCards,
});

</script>

<style lang="scss">
@import "@/assets/style/include";

.pagination {
  display: flex;
  justify-content: center;
  margin-top: 8px;

  &-item {
    padding: 6px;
    cursor: pointer;

    &:before {
      width: 10px;
      height: 10px;
      content: '';
      display: block;
      border: 1px solid $colorBlue1;
      transform: rotate(45deg);
      transition: background .2s;
    }

    &.disabled {
      cursor: auto;
      &:before {
        background: $colorBlue1;
        border-color: $colorBlue1;
      }
    }

    &:hover {
      &:before {
        background: $colorBlueHover;
        border-color: $colorBlueHover;
      }
    }

    &:active {
      &:before {
        background: $colorBluePressed;
        border-color: $colorBluePressed;
      }
    }
  }
}
</style>
